/* @flow */

/**
 * @class
 * @description Utilities for working with iframes
 * @module utils/iframeutils
 * @requires module:coreutils
 */

import { isString, isHTMLIFrameElement } from './coreutils';
/**
 * Get the iframe/window document
 *
 * @param {HTMLIFrameElement} tempIFrameObj - IFrame element
 * @returns {Document} - IFrame document
 */
export function getIfrDoc(tempIFrameObj: HTMLIFrameElement): ?Document {
  let tempIFrameDoc: ?Document = null;

  if (!tempIFrameObj) {
    return tempIFrameDoc;
  }
  // get the document
  if (tempIFrameObj.contentDocument) {
    tempIFrameDoc = tempIFrameObj.contentDocument;
  } else if (
    tempIFrameObj.contentWindow &&
    tempIFrameObj.contentWindow.document
  ) {
    tempIFrameDoc = tempIFrameObj.contentWindow.document;
  } else if (
    // $FlowFixMe[prop-missing]
    tempIFrameObj.document !== null &&
    // $FlowFixMe[prop-missing]
    tempIFrameObj.document !== undefined
  ) {
    // $FlowFixMe[incompatible-type]
    tempIFrameDoc = tempIFrameObj.document;
  }
  return tempIFrameDoc;
}
/**
 * clean ad frame and container
 *
 * @param {Document} doc - Document DOM element
 * @param {HTMLElement} adDivObj - ad div obj
 * @param {string} adFrameID - iframe id
 * @returns {undefined}
 * @private
 */
export function cleanFrame(
  doc: Document,
  adDivObj: HTMLElement,
  adFrameID: string
) {
  if (adFrameID.length) {
    const oldIF = doc.getElementById(adFrameID);
    // clean up old iframe
    if (isHTMLIFrameElement(oldIF)) {
      // $FlowFixMe[incompatible-type]
      const iframe: HTMLIFrameElement = oldIF;
      iframe.src = '';
      if (
        // $FlowFixMe[prop-missing]
        iframe.documentWindow !== null &&
        // $FlowFixMe[prop-missing]
        iframe.documentWindow !== undefined
      ) {
        // $FlowFixMe[incompatible-use]
        iframe.documentWindow.document = null;
        // $FlowFixMe[prop-missing]
        iframe.documentWindow = null;
      }
    }
  }

  // empty content of the ad wrapper div now
  try {
    /* eslint-disable no-unmodified-loop-condition */
    // eslint-disable-next-line no-restricted-syntax
    while (adDivObj && adDivObj.firstChild) {
      /* eslint-enable no-unmodified-loop-condition */
      // eslint-disable-next-line unicorn/prefer-dom-node-remove
      adDivObj.removeChild(adDivObj.firstChild);
    }
  } catch {
    /* Ignore exception here */
  }
}

/**
 * Set iframe height by calculating the dynamic height for the iframe.
 *
 * @param {string} id - IFrame DOM ID
 * @returns {undefined}
 */
export function setIfrHeight(id: string) {
  /** Flow doesn't differentiate HTMLIFrameElement from HTMLElement
   * as we are using getElementById here. Hence override it
   * https://stackoverflow.com/questions/40052760/flow-htmlelement-queryselector-returning-an-iframe
   */
  const currrentObj = document.getElementById(id);
  if (!isHTMLIFrameElement(currrentObj)) {
    return;
  }
  // $FlowFixMe[incompatible-type]
  const iframe: HTMLIFrameElement = currrentObj;
  // $FlowFixMe[incompatible-type]
  const tempIFrameDoc = getIfrDoc(iframe);
  if (tempIFrameDoc && tempIFrameDoc.body) {
    // Put max of scrollHeight & offsetheight to make it work cross-browser
    // $FlowFixMe[incompatible-type]
    iframe.height = String(
      Math.max(tempIFrameDoc.body.scrollHeight, tempIFrameDoc.body.offsetHeight)
    );
    if (tempIFrameDoc && tempIFrameDoc.body && tempIFrameDoc.body.style) {
      tempIFrameDoc.body.style.marginBottom = '0px';
      tempIFrameDoc.body.style.marginRight = '0px';
    }
  }
}

/**
 * Create iframe element
 *
 * @param {Document} doc -Document DOM object
 * @param {string} id - new IFrame ID
 * @param {number} width - width
 * @param {number} height - height
 * @param {string} [url] - URL to load
 * @param {string} [sandbox] - sandbox attribute
 * @returns {HTMLIFrameElement} - new IFrame element
 */
export function createIframe(
  doc: Document,
  id: string,
  width: string | number,
  height: string | number,
  url?: string = '',
  sandbox?: string = ''
): HTMLIFrameElement {
  const newIF = doc.createElement('iframe');

  // set property for new iframe
  newIF.setAttribute('id', id);
  newIF.setAttribute('name', id);
  newIF.setAttribute('topmargin', '0');
  newIF.setAttribute('leftmargin', '0');
  newIF.setAttribute('marginheight', '0');
  newIF.setAttribute('marginwidth', '0');
  newIF.width = String(width);
  newIF.height = String(height);
  newIF.frameBorder = '0';
  newIF.scrolling = 'no';
  // $FlowFixMe[prop-missing]
  newIF.allowTransparency = 'true';
  // $FlowFixMe[prop-missing]
  newIF.backgroundColor = 'transparant';

  // setting sandbox attribute only if it is defined
  if (isString(sandbox) && sandbox.length > 0) {
    newIF.setAttribute('sandbox', sandbox);
  }

  if (isString(url) && url.length) {
    newIF.src = url;
  }
  return newIF;
}
